import './styles.css';
import { buildQuizPack } from './quiz-pack';
import { generatePrintableHtml } from './pdf-gen';
import { gradeScannedPaper } from './scan-grader';
import { loadProgress, saveProgress, createFreshProgress } from '../../shared/progress';
import { findOrCreateKP, recordAttempt } from '../../shared/student-kp';
import type { Subject } from '../../shared/types';

const SUBJECT_KP_OPTIONS: { subject: Subject; kp: string; label: string }[] = [
  { subject: 'math', kp: 'math.mul.2x1', label: '數學:乘法 2x1' },
  { subject: 'math', kp: 'math.mul.2x2', label: '數學:乘法 2x2' },
  { subject: 'math', kp: 'math.div.remainder', label: '數學:除法餘數' },
  { subject: 'chinese', kp: 'chinese.zhuyin.basic', label: '國語:注音' },
  { subject: 'english', kp: 'english.alphabet.phonics', label: '英語:字母拼讀' },
  { subject: 'nature', kp: 'nature.water.states', label: '自然:水的三態' },
  { subject: 'social', kp: 'social.community.basic', label: '社會:社區常識' },
];

export function renderPrintableMode(container: HTMLElement, onBack: () => void): void {
  const root = document.createElement('div');
  root.className = 'printable-mode';
  container.appendChild(root);

  function renderPicker(): void {
    root.innerHTML = `
      <h1>📄 列印練習卷</h1>
      <p>選一個 KP,生成 A4 練習卷,印出來給小孩寫(每張 10 題,大約 5-10 分鐘)</p>
      <div class="printable-kp-grid"></div>
      <button class="secondary-btn" id="back">回主畫面</button>
    `;
    const grid = root.querySelector('.printable-kp-grid')!;
    for (const opt of SUBJECT_KP_OPTIONS) {
      const card = document.createElement('button');
      card.className = 'printable-kp-card';
      card.textContent = opt.label;
      card.addEventListener('click', () => renderGenerate(opt.subject, opt.kp));
      grid.appendChild(card);
    }
    root.querySelector('#back')!.addEventListener('click', () => { root.remove(); onBack(); });
  }

  async function renderGenerate(subject: Subject, kpId: string): Promise<void> {
    root.innerHTML = `<h2>生成中...</h2>`;
    try {
      const pack = await buildQuizPack(subject, kpId, 10);
      // Stash the pack so the upload step can find it (sessionStorage)
      sessionStorage.setItem(`pack:${pack.packId}`, JSON.stringify(pack));

      // Open the printable HTML in a new window immediately
      const html = generatePrintableHtml(pack);
      const w = window.open('', '_blank');
      if (!w) {
        alert('請允許彈出視窗才能列印練習卷');
      } else {
        w.document.write(html);
        w.document.close();
      }

      // Show upload section right away (parent can print + write while upload section waits)
      root.innerHTML = `
        <h2>卷號 ${pack.packId}</h2>
        <p>練習卷已在新視窗開啟(${pack.questions.length} 題),請在新視窗點「列印」。</p>
        <p>小孩寫完後,點下面上傳掃描照片打分:</p>
        <input type="file" accept="image/*" id="scan-file" />
        <div id="scan-status"></div>
        <button class="secondary-btn" id="back">回主畫面</button>
      `;

      root.querySelector('#back')!.addEventListener('click', () => {
        root.remove();
        onBack();
      });

      const fileInput = root.querySelector('#scan-file') as HTMLInputElement;
      const status = root.querySelector('#scan-status')!;

      fileInput.addEventListener('change', async () => {
        const file = fileInput.files?.[0];
        if (!file) return;
        status.innerHTML = '<p>🔍 AI 判讀中(約 15-30 秒)...</p>';
        try {
          const result = await gradeScannedPaper(file, pack);
          // Write to StudentKP
          const progress = loadProgress() ?? createFreshProgress('指揮官');
          const kp = findOrCreateKP(progress, kpId, subject, 'exam-upload');
          const now = new Date().toISOString();
          for (const r of result.perQuestion) {
            recordAttempt(kp, r.correct ? 'correct' : 'wrong', now);
          }
          saveProgress(progress);
          status.innerHTML = `
            <h2>結果:對 ${result.correctCount} / ${result.totalCount} 題</h2>
            <ol>${result.perQuestion.map(r =>
              `<li>${r.idx}. ${r.correct ? '✅' : '❌'} 寫了「${r.kidAnswer}」,正解「${r.expected}」</li>`
            ).join('')}</ol>
          `;
        } catch (e) {
          status.innerHTML = `<p style="color:#ef5350;">❌ 失敗:${(e as Error).message}</p>`;
        }
      });
    } catch (e) {
      root.innerHTML = `
        <p style="color:#ef5350;">❌ 生成失敗:${(e as Error).message}</p>
        <button class="secondary-btn" id="back">回主畫面</button>
      `;
      root.querySelector('#back')!.addEventListener('click', () => { root.remove(); onBack(); });
    }
  }

  renderPicker();
}
